import { useState, useEffect } from "react";

import {
  abandonGameSession,
  fetchRevisioCollections,
  fetchRevisioSession,
  finishRevisio,
  startRevisio,
  submitRevisioAttempt,
} from "./api";
import {
  AbandonActivityButton,
  AbandonedActivityPanel,
  ActivityGuide,
  defaultGuide,
  FeedbackNotice,
  RewardSummary,
  SessionProgressBar,
} from "./Experience";
import { navigate } from "./navigation";
import { subscribeScoreInvalidations } from "./score";
import { FaceView, QuestionAnswer } from "./OtherGames";
import type {
  ExperienceFeedback,
  ExperienceProgress,
  ExperienceReward,
  MemoResult,
  RevisioCollectionSummary,
  RevisioSession,
  UserScoreSnapshot,
} from "./types";

export function ReviewLibrary() {
  const [collections, setCollections] = useState<RevisioCollectionSummary[] | null>(null);
  const [totals, setTotals] = useState({ cards: 0, points: 0 });
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [revision, setRevision] = useState(0);

  useEffect(() => subscribeScoreInvalidations(() => {
    setPage(1);
    setRevision((value) => value + 1);
  }), []);

  useEffect(() => {
    let active = true;
    void fetchRevisioCollections()
      .then((payload) => {
        if (!active) return;
        setCollections(payload.collections);
        setTotals({ cards: payload.due_cards_count, points: payload.earnable_points });
        setHasNext(payload.pagination.has_next);
      })
      .catch((reason: unknown) => {
        if (active) setError(reason instanceof Error ? reason.message : "Révisions indisponibles.");
      });
    return () => {
      active = false;
    };
  }, [revision]);

  const loadMore = async () => {
    if (!hasNext || loadingMore) return;
    setLoadingMore(true);
    setError(null);
    try {
      const nextPage = page + 1;
      const payload = await fetchRevisioCollections(nextPage);
      setCollections((current) => [...(current ?? []), ...payload.collections]);
      setPage(nextPage);
      setHasNext(payload.pagination.has_next);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Page suivante indisponible.");
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <section className="collection-page">
      <button className="back-button" type="button" onClick={() => navigate("/")}>
        ← Retour au tableau de bord
      </button>
      <div className="collection-heading">
        <div>
          <p className="eyebrow">Progression espacée</p>
          <h1>Mes révisions</h1>
          <p>Retrouvez uniquement les cartes dont la prochaine maîtrise est disponible.</p>
        </div>
        <span className="section-chip">
          {totals.cards} carte{totals.cards > 1 ? "s" : ""} · {totals.points} points
        </span>
      </div>
      {error && <div className="info-notice">{error}</div>}
      {collections === null && !error ? (
        <div className="collection-loading">Calcul de vos échéances…</div>
      ) : collections?.length ? (
        <>
          <div className="collection-grid">
            {collections.map((collection) => (
            <article className="collection-tile" key={collection.id}>
              <div className="collection-tile-top">
                <span
                  className="subject-color"
                  style={{ backgroundColor: collection.subject.color }}
                  aria-hidden="true"
                />
                <span>{collection.subject.name}</span>
              </div>
              <h2>{collection.title}</h2>
              <p>{collection.description ?? "Cette série est prête à être révisée."}</p>
              <div className="collection-meta">
                <span>{collection.due_cards_count} à revoir</span>
                <span>{collection.earnable_points} points possibles</span>
              </div>
              <button
                className="button button-primary"
                type="button"
                onClick={() => navigate(collection.path)}
              >
                Commencer la révision
              </button>
            </article>
            ))}
          </div>
          {hasNext && (
            <button className="button" type="button" disabled={loadingMore} onClick={() => void loadMore()}>
              {loadingMore ? "Chargement…" : "Afficher plus de révisions"}
            </button>
          )}
        </>
      ) : (
        <div className="empty-state collection-empty">
          <strong>Aucune carte à réviser pour le moment</strong>
          <p>Jouez avec vos séries : les prochaines échéances apparaîtront ici.</p>
        </div>
      )}
    </section>
  );
}

export function RevisioGame({ collectionId, initialSessionToken, onScoreUpdated }: { collectionId: number; initialSessionToken?: string; onScoreUpdated: (score: UserScoreSnapshot) => void }) {
  const [session, setSession] = useState<RevisioSession | null>(null);
  const [cardIndex, setCardIndex] = useState(0);
  const [text, setText] = useState("");
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [answers, setAnswers] = useState(0);
  const [errors, setErrors] = useState(0);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [experienceFeedback, setExperienceFeedback] = useState<ExperienceFeedback | null>(null);
  const [progress, setProgress] = useState<ExperienceProgress | undefined>();
  const [reward, setReward] = useState<ExperienceReward | null>(null);
  const [result, setResult] = useState<MemoResult | null>(null);
  const [abandoned, setAbandoned] = useState<ExperienceFeedback | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!initialSessionToken) return;
    let active = true;
    setLoading(true);
    void fetchRevisioSession(initialSessionToken)
      .then((next) => {
        if (!active) return;
        setSession(next);
        setAnswers(next.stats?.answers ?? 0);
        setErrors(next.stats?.errors ?? 0);
        setCardIndex(next.attempts_count ?? 0);
        setResult(next.result ?? null);
        setProgress(next.progress);
        setExperienceFeedback(null);
      })
      .catch((reason: unknown) => {
        if (active) setError(reason instanceof Error ? reason.message : "Impossible de reprendre la révision.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [initialSessionToken]);

  const begin = async () => {
    setLoading(true);
    setError(null);
    try {
      const next = await startRevisio(collectionId);
      setSession(next);
      setCardIndex(0);
      setAnswers(0);
      setErrors(0);
      setFeedback(null);
      setResult(null);
      setProgress(next.progress);
      setExperienceFeedback(null);
      setReward(null);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Impossible de préparer la révision.");
    } finally {
      setLoading(false);
    }
  };

  const question = session?.cards[cardIndex];
  const submit = async (answer: unknown, responseMode?: "INPUT" | "QCM") => {
    if (!session || !question || loading) return;
    setLoading(true);
    setError(null);
    try {
      const response = await submitRevisioAttempt(session.token, {
        card_id: question.id,
        answer,
        response_mode: responseMode,
      });
      setAnswers(response.stats.answers);
      setErrors(response.stats.errors);
      setFeedback(
        response.correct
          ? `Bonne réponse · maîtrise ${response.mastery.from} → ${response.mastery.to}`
          : `À retravailler · maîtrise ${response.mastery.from} → ${response.mastery.to}`,
      );
      setExperienceFeedback(response.feedback ?? null);
      setProgress(response.progress);
      setReward(response.reward ?? null);
      setText("");
      setSelectedIds([]);
      if (response.finished) {
        setResult(response.result);
        if (response.user_score) onScoreUpdated(response.user_score);
      }
      else setCardIndex((value) => value + 1);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Réponse non enregistrée.");
    } finally {
      setLoading(false);
    }
  };

  const stop = async () => {
    if (!session || answers + errors === 0 || loading) return;
    setLoading(true);
    try {
      const response = await finishRevisio(session.token);
      setResult(response.result);
      setExperienceFeedback(response.feedback ?? null);
      setProgress(response.progress);
      setReward(response.reward ?? null);
      onScoreUpdated(response.user_score);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Fin de révision non enregistrée.");
    } finally {
      setLoading(false);
    }
  };

  const abandon = async () => {
    if (!session) return;
    setError(null);
    try {
      const response = await abandonGameSession(session.token);
      setAbandoned(response.feedback);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Impossible d’interrompre la révision.");
    }
  };

  const pass = () => {
    if (!session || !question || answers + errors >= session.target_count - 1) return;
    const cards = [...session.cards];
    const [skipped] = cards.splice(cardIndex, 1);
    if (skipped) cards.push(skipped);
    setSession({ ...session, cards });
    setFeedback("Carte déplacée à la fin de la révision.");
    setText("");
    setSelectedIds([]);
  };

  return (
    <section className="memo-page">
      <button className="back-button" type="button" onClick={() => navigate("/mes-revisions")}>
        ← Retour à mes révisions
      </button>
      {abandoned ? (
        <AbandonedActivityPanel feedback={abandoned} onReturn={() => navigate("/mes-revisions")} />
      ) : result ? (
        <div className={`memo-panel memo-result ${result.won ? "won" : "lost"}`}>
          <ActivityGuide guide={session?.guide} activeStep={4} />
          <p className="eyebrow">Revisio · session terminée</p>
          <h1>{result.won ? "Progression enregistrée" : "À reprendre bientôt"}</h1>
          <strong className="memo-score">{result.score} point{result.score > 1 ? "s" : ""}</strong>
          <p>Durée enregistrée par Cogito : {result.time} seconde{result.time > 1 ? "s" : ""}.</p>
          <RewardSummary reward={reward} />
          <div className="memo-result-actions">
            <button className="button button-primary" type="button" onClick={() => { setSession(null); setResult(null); }}>
              Revoir les échéances
            </button>
            <button className="button" type="button" onClick={() => navigate("/mes-revisions")}>
              Mes révisions
            </button>
          </div>
        </div>
      ) : !session ? (
        <div className="memo-panel memo-setup">
          <ActivityGuide guide={{ ...defaultGuide, target_count: 1, estimated_minutes: 2 }} activeStep={0} />
          <p className="eyebrow">Révision espacée</p>
          <h1>Revisio</h1>
          <p className="lead">
            Chaque carte due est présentée une fois. Une bonne réponse peut augmenter sa maîtrise ;
            une erreur la replace au niveau initial.
          </p>
          {error && <p className="game-error">{error}</p>}
          <button className="button button-primary" type="button" disabled={loading} onClick={() => void begin()}>
            {loading ? "Préparation…" : "Commencer"}
          </button>
        </div>
      ) : (
        <div className="memo-panel practice-panel">
          <ActivityGuide guide={session.guide} activeStep={experienceFeedback ? 3 : 2} />
          <SessionProgressBar progress={progress} />
          <div className="memo-status">
            <div><span>Bonnes réponses</span><strong>{answers}</strong></div>
            <div><span>Erreurs</span><strong>{errors}</strong></div>
            <div><span>Progression</span><strong>{cardIndex + 1}/{session.target_count}</strong></div>
          </div>
          {question && (
            <>
              <div className="memo-question">
                <p className="card-side-label">Question</p>
                <FaceView face={question.front} />
              </div>
              <QuestionAnswer
                question={question}
                text={text}
                selectedIds={selectedIds}
                disabled={loading}
                setText={setText}
                toggleId={(id) => setSelectedIds((values) => values.includes(id) ? values.filter((value) => value !== id) : [...values, id])}
                submit={(answer, mode) => void submit(answer, mode)}
              />
            </>
          )}
          {!experienceFeedback && <p className={feedback?.startsWith("Bonne") ? "game-feedback success" : "game-feedback"}>{feedback}</p>}
          <FeedbackNotice feedback={experienceFeedback} />
          {error && <p className="game-error">{error}</p>}
          <div className="memo-result-actions">
            <button className="button" type="button" disabled={loading || answers + errors >= session.target_count - 1} onClick={pass}>
              Passer cette carte
            </button>
            <button className="button" type="button" disabled={loading || answers + errors === 0} onClick={() => void stop()}>
              Terminer maintenant
            </button>
            <AbandonActivityButton disabled={loading} onConfirm={abandon} />
          </div>
        </div>
      )}
    </section>
  );
}
